import { Tooltip } from 'antd'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'

import { ServerPrivateRegionNodeSpec, ServerPrivateRegionVGpuSpec } from '@/api/type-server'
import InfoIcon from '@/assets/icons/info-i.svg?react'

import { SpecCategory, SpecCategoryType } from '../constants/specCategory'
import {
  AllocationDisplay,
  formatCpuAllocation,
  formatMemoryAllocation,
  formatStorageAllocation,
} from '../utils/nodeFormatters'

interface ReadOnlySpecSectionProps {
  specs: ServerPrivateRegionNodeSpec[]
  vgpuSpecs?: ServerPrivateRegionVGpuSpec[]
}

// Category label mapping
const CATEGORY_LABELS: Record<SpecCategoryType, string> = {
  [SpecCategory.VGPU]: 'vGPU',
  [SpecCategory.GPU]: 'GPU',
  [SpecCategory.CPU]: 'vCPU',
}

// Allocation value display component
function AllocationValue({ allocation }: { allocation: AllocationDisplay }) {
  return (
    <div className="t-sb text-primary font-bold">
      {allocation.value}
      {allocation.subValue && <span className="text-tertiary ml-1">{allocation.subValue}</span>}
    </div>
  )
}

// Field with optional tooltip
function FieldWithTooltip({
  label,
  tooltipKey,
  children,
}: {
  label: string
  tooltipKey?: string
  children: React.ReactNode
}) {
  const { t } = useTranslation('privateRegion')
  return (
    <div className="flex-1 min-w-[226px] flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <span className="t-xs text-secondary font-semibold">{label}</span>
        {tooltipKey && (
          <Tooltip
            title={t(tooltipKey)}
            classNames={{
              root: 't-xs max-w-[180px] rounded-md',
            }}
            styles={{
              body: { textAlign: 'center' },
            }}
          >
            <InfoIcon className="t-xs text-secondary" />
          </Tooltip>
        )}
      </div>
      {children}
    </div>
  )
}

// Simple field without tooltip
function SimpleField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex-1 min-w-[226px] flex flex-col gap-1">
      <span className="t-xs text-secondary font-semibold">{label}</span>
      {children}
    </div>
  )
}

// Empty placeholder to maintain grid alignment
function EmptyField() {
  return <div className="flex-1 min-w-[226px]" />
}

// Single spec row component
function SpecRow({
  spec,
  category,
  vgpuSpecs,
}: {
  spec: ServerPrivateRegionNodeSpec
  category: SpecCategoryType
  vgpuSpecs?: ServerPrivateRegionVGpuSpec[]
}) {
  const { t } = useTranslation('privateRegion')

  const cpuAlloc = formatCpuAllocation(spec.cpuCores, spec.totalCpuCores)
  const memAlloc = formatMemoryAllocation(spec.ramMB, spec.totalRamMB)
  const storageAlloc = formatStorageAllocation(spec.diskGB, spec.totalDiskGB)

  const isVGPU = category === SpecCategory.VGPU
  const isGPU = category === SpecCategory.GPU
  const isCPU = category === SpecCategory.CPU

  return (
    <div className="flex flex-col gap-4">
      {/* Row 1: Spec Unit, GPU Number (vGPU/GPU), placeholders */}
      <div className="flex gap-4">
        <FieldWithTooltip
          label={t('instanceSpec.specUnit')}
          tooltipKey="instanceSpec.specUnitTooltip"
        >
          <div className="t-sb text-primary font-bold">{spec.units}</div>
        </FieldWithTooltip>

        {(isVGPU || isGPU) && (
          <FieldWithTooltip
            label={t(isVGPU ? 'instanceSpec.vgpuNumber' : 'instanceSpec.gpuNumber')}
            tooltipKey={isVGPU ? 'instanceSpec.vgpuNumberTooltip' : 'instanceSpec.gpuNumberTooltip'}
          >
            <div className="t-sb text-primary font-bold">{spec.gpuNum}</div>
          </FieldWithTooltip>
        )}

        {/* vGPU GPC - fills 3rd column in vGPU mode */}
        {isVGPU && (
          <FieldWithTooltip
            label={t('instanceSpec.vgpuGpc')}
            tooltipKey="instanceSpec.vgpuGpcTooltip"
          >
            <div className="t-sb text-primary font-bold">
              {vgpuSpecs?.find((s) => s.resID === spec.vgpuSpecId)?.title ?? '-'}
            </div>
          </FieldWithTooltip>
        )}

        {/* Placeholders to maintain 3-column layout */}
        {isGPU && <EmptyField />}
        {isCPU && (
          <>
            <EmptyField />
            <EmptyField />
          </>
        )}
      </div>

      {/* Row 2: vCPU Allocation, Memory Allocation, Storage Allocation */}
      <div className="flex gap-4">
        <SimpleField label={t('instanceSpec.vcpuAllocation')}>
          <AllocationValue allocation={cpuAlloc} />
        </SimpleField>
        <SimpleField label={t('instanceSpec.memoryAllocation')}>
          <AllocationValue allocation={memAlloc} />
        </SimpleField>
        <SimpleField label={t('instanceSpec.storageAllocation')}>
          <AllocationValue allocation={storageAlloc} />
        </SimpleField>
      </div>
    </div>
  )
}

// Category section with divider and specs
function CategorySection({
  label,
  specs,
  category,
  isFirst,
  vgpuSpecs,
}: {
  label: string
  specs: ServerPrivateRegionNodeSpec[]
  category: SpecCategoryType
  isFirst: boolean
  vgpuSpecs?: ServerPrivateRegionVGpuSpec[]
}) {
  if (specs.length === 0) return null

  return (
    <div className="flex flex-col gap-4">
      {/* Section Divider */}
      <div className={`flex gap-2 items-center w-full ${!isFirst ? 'pt-2' : ''}`}>
        <span className="t-xs text-tertiary font-semibold">{label}</span>
        <div className="flex-1 h-px bg-neutral-200" />
      </div>

      {/* Spec Rows */}
      {specs.map((spec) => (
        <SpecRow key={spec.resID} spec={spec} category={category} vgpuSpecs={vgpuSpecs} />
      ))}
    </div>
  )
}

export const ReadOnlySpecSection = memo(function ReadOnlySpecSection({
  specs,
  vgpuSpecs: vgpuSpecOptions,
}: ReadOnlySpecSectionProps) {
  // Filter enabled specs by category
  const enabledSpecs = specs.filter((s) => s.enabled)
  const vgpuSpecs = enabledSpecs.filter((s) => s.category === SpecCategory.VGPU)
  const gpuSpecs = enabledSpecs.filter((s) => s.category === SpecCategory.GPU)
  const cpuSpecs = enabledSpecs.filter((s) => s.category === SpecCategory.CPU)

  // Determine which category is first (for proper spacing)
  const hasVgpu = vgpuSpecs.length > 0
  const hasGpu = gpuSpecs.length > 0

  return (
    <div className="bg-panel-white border border-neutral-200 rounded-lg px-6 py-4">
      <div className="flex flex-col gap-4">
        {/* vGPU Section */}
        <CategorySection
          label={CATEGORY_LABELS[SpecCategory.VGPU]}
          specs={vgpuSpecs}
          category={SpecCategory.VGPU}
          isFirst={true}
          vgpuSpecs={vgpuSpecOptions}
        />

        {/* GPU Section */}
        <CategorySection
          label={CATEGORY_LABELS[SpecCategory.GPU]}
          specs={gpuSpecs}
          category={SpecCategory.GPU}
          isFirst={!hasVgpu}
        />

        {/* vCPU Section */}
        <CategorySection
          label={CATEGORY_LABELS[SpecCategory.CPU]}
          specs={cpuSpecs}
          category={SpecCategory.CPU}
          isFirst={!hasVgpu && !hasGpu}
        />
      </div>
    </div>
  )
})
